import {LocaleMap} from '@unifocus/js-utils/dist/types/localization/FormatMessage';
import React from 'react';
import ReactDOM from 'react-dom';
import {initializeReactEnvironment, initializeReactTestEnvironment} from './ReactEnvironment';
import {getBaseHash} from '../router/Router';
import {initializeMessages} from '@unifocus/js-utils';
import '@unifocus/react-application/dist/lib/react-application.less';

/**
 * {@docCategory bootstrapApplication}
 */
export interface BootstrapApplicationOptions {
   /**
    * Sets the applicationId. This is either a string or a function that takes a route and returns
    * the applicationId. If you use the function that takes the route, note that the route is always
    * the base part of the route. For example, if the URL looks like "https://test.unifocus.com/test/index.html/#/main/123"
    * then the route passed to the function will be "/main".
    */
   applicationId: ((route: string) => string) | string;

   /**
    * This specifies the localization messages.
    */
   messages: LocaleMap;

   /**
    * A function that returns a Promise that will then return the rootComponentModule.
    * This will only be called after the application environment has been initialized.
    *
    * The idea is that you will create a function that uses a dynamic import to return the main
    * application module. This module must have the root component exported as the default for the module.
    */
   rootComponentModule: () => Promise<{ default: React.ComponentType; }>;
}

export const bootstrapApplication = (options: BootstrapApplicationOptions) => {
   initializeMessages(options.messages);

   initializeReactEnvironment(getApplicationId(options), () => {
      options.rootComponentModule().then((rootComponentModule) => {
         ReactDOM.render(
            <rootComponentModule.default/>,
            document.getElementById('root') as HTMLElement
         );
      });
   });
};

export const bootstrapApplicationTest = (options: BootstrapApplicationOptions) => {
   initializeMessages(options.messages);

   initializeReactTestEnvironment(getApplicationId(options), () => {
      options.rootComponentModule().then((rootComponentModule) => {
         console.debug(`Rendering the default component from the rootComponentModule`);

         ReactDOM.render(
            <React.StrictMode>
               <rootComponentModule.default/>
            </React.StrictMode>,
            document.getElementById('root') as HTMLElement
         );
      });
   });
};

const getApplicationId = (options: BootstrapApplicationOptions) => {
   return typeof options.applicationId === 'string' ? options.applicationId : options.applicationId(
      getBaseHash());
};
